import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha, createTheme, ThemeProvider } from '@mui/material/styles';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import DevicesOtherOutlinedIcon from '@mui/icons-material/DevicesOtherOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import HistoryIcon from '@mui/icons-material/History';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import CopyableText from '../common/CopyableText';
import { Repair } from '../../types/typesRepair';
import { Customer } from '../../types/typesCustomer';
import StatusStepper from './StatusStepper';
import PhoneLinkChip from '../common/PhoneLinkChip';
import { formatDateTime, formatDate } from '../../utils/date';
import {
  formatRepairStatusForCustomer,
  normalizeRepairStatusCanonical,
} from '../../utils/repairStatusDisplay';
import { getRepairStatusColors } from '../../utils/repairStatusColors';
import { buildRepairStatusDates } from '../../utils/repairStatusDates';
import { resolveRepairPaymentDisplayState } from '../../utils/repairPaymentDisplay';
import { useThemeMode } from '../../contexts/ThemeContext';
import { paymentService } from '../../services/PaymentService';

/** Game Star look for public /track page only */
const publicTrackThemeDark = createTheme({
  direction: 'rtl',
  palette: {
    mode: 'dark',
    primary: {
      main: '#FFD000',
      light: '#FFE033',
      dark: '#E6BB00',
      contrastText: '#1a1a1a',
    },
    secondary: {
      main: '#9e9e9e',
      contrastText: '#111',
    },
    background: {
      default: '#0a0a0a',
      paper: '#1a1a1a',
    },
    text: {
      primary: '#ffffff',
      secondary: '#b0b0b0',
      disabled: '#808080',
    },
    divider: 'rgba(255,255,255,0.12)',
    warning: {
      main: '#FFD000',
      contrastText: '#1a1a1a',
    },
  },
  typography: {
    fontFamily: '"Heebo", "Assistant", "Rubik", "Segoe UI", Arial, sans-serif',
  },
  shape: { borderRadius: 10 },
});

const publicTrackThemeLight = createTheme({
  direction: 'rtl',
  palette: {
    mode: 'light',
    primary: {
      main: '#C9A000',
      light: '#E6BB00',
      dark: '#A68600',
      contrastText: '#1a1a1a',
    },
    secondary: {
      main: '#64748b',
      contrastText: '#fff',
    },
    background: {
      default: '#f3f4f6',
      paper: '#ffffff',
    },
    text: {
      primary: '#1a1a1a',
      secondary: '#5c5c5c',
      disabled: '#8a8a8a',
    },
    divider: 'rgba(0,0,0,0.1)',
    warning: {
      main: '#C9A000',
      contrastText: '#1a1a1a',
    },
  },
  typography: {
    fontFamily: '"Heebo", "Assistant", "Rubik", "Segoe UI", Arial, sans-serif',
  },
  shape: { borderRadius: 10 },
});

interface ExternalRepairViewProps {
  repair: Repair;
  customer?: Customer | null;
  callNumber?: string;
  onOpenRepairHistory?: () => void;
  /** מעקב ציבורי: סיווגי תקלה בלבד (בלי טקסט חופשי), וסיכום טיפול סופי בלי סיכום מעבדה */
  publicTrackMode?: boolean;
}

function InfoRow({
  label,
  children,
  hideDivider,
}: {
  label: string;
  children: React.ReactNode;
  hideDivider?: boolean;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        py: 1,
        borderBottom: hideDivider ? 'none' : '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ minWidth: { xs: 72, sm: 88 }, flexShrink: 0, pt: 0.1, fontSize: '0.8125rem' }}
      >
        {label}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
    </Box>
  );
}

function SectionCard({
  icon,
  title,
  children,
  headerExtra,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
  headerExtra?: React.ReactNode;
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2.5,
        overflow: 'hidden',
        height: '100%',
        bgcolor: 'background.paper',
        borderColor: 'divider',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 1.75,
          py: 1.25,
          bgcolor: (t) =>
            t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flexWrap: 'wrap' }}>
          <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center', flexShrink: 0 }}>{icon}</Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9375rem', color: 'text.primary' }}>
            {title}
          </Typography>
        </Box>
        {headerExtra ? <Box sx={{ flexShrink: 0 }}>{headerExtra}</Box> : null}
      </Box>
      <Box sx={{ px: 1.75, py: 0.5 }}>{children}</Box>
    </Paper>
  );
}

const REPAIRED_STATUS_LABELS: Record<string, { label: string; color: 'success' | 'error' | 'warning' | 'default' }> = {
  כן: { label: 'תוקן', color: 'success' },
  לא: { label: 'לא תוקן', color: 'error' },
  חלקי: { label: 'תיקון חלקי', color: 'warning' },
};

function formatTimestamp(iso?: string | null): string | null {
  const raw = String(iso || '').trim();
  if (!raw) return null;
  const formatted = formatDateTime(raw);
  return formatted || null;
}

function DateCaption({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: 'block', mt: 0.75, fontSize: '0.75rem' }}
    >
      {children}
    </Typography>
  );
}

const ExternalRepairViewInner: React.FC<ExternalRepairViewProps> = ({
  repair,
  customer,
  callNumber,
  onOpenRepairHistory,
  publicTrackMode = false,
}) => {
  const theme = useTheme();
  const statusMode = theme.palette.mode === 'dark' ? 'dark' : 'light';
  const statusCanonical = normalizeRepairStatusCanonical(repair.status);
  const statusLabel = formatRepairStatusForCustomer(repair.status);
  const statusColors = getRepairStatusColors(statusCanonical || statusLabel, statusMode);

  const displayCallNumber = callNumber || repair.callNumber || '—';
  const copyableCallNumber = displayCallNumber !== '—' ? String(displayCallNumber).trim() : '';

  const customerName = useMemo(() => {
    const fromCustomer = [customer?.firstName, customer?.lastName].filter(Boolean).join(' ').trim();
    if (fromCustomer) return fromCustomer;
    return String(repair.customerName || '').trim() || '—';
  }, [customer?.firstName, customer?.lastName, repair.customerName]);

  const phone = String(customer?.phone || repair.customerPhone || '').trim();
  const deviceLabel = String(repair.deviceModel || repair.model || '').trim() || '—';
  const issueText = String(repair.issue || '').trim();
  const issueTypes = Array.isArray(repair.issueTypes) ? repair.issueTypes.filter(Boolean) : [];
  const accessories = Array.isArray(repair.accessories) ? repair.accessories.filter(Boolean) : [];

  const labSummary = String(repair.labSummary || '').trim();
  const serviceSummary = String(repair.serviceSummary || '').trim();
  const treatmentSummary = publicTrackMode ? serviceSummary : labSummary || serviceSummary;
  const showIssueText = Boolean(issueText) && !publicTrackMode;
  const showIssueSection = publicTrackMode ? issueTypes.length > 0 : issueTypes.length > 0 || Boolean(issueText);

  const issueWrittenAt = formatTimestamp(repair.receivedDate || repair.createdAt);

  const treatmentSummaryAt = formatTimestamp(
    publicTrackMode
      ? repair.labResultStatusDate || repair.technicianCompletedDate || repair.treatmentEndDate
      : labSummary
        ? repair.labSummaryDate
        : serviceSummary
          ? repair.labResultStatusDate || repair.technicianCompletedDate
          : null,
  );

  const isReturnedToCustomer =
    statusCanonical === 'חזר ללקוח' || repair.status === 'חזר לחנות';
  const returnedToCustomerAt = formatTimestamp(
    repair.returnedToCustomerDate ||
      (isReturnedToCustomer ? repair.statusChangedAt : null),
  );

  const spareParts = Array.isArray(repair.sparePartsWithQty)
    ? repair.sparePartsWithQty.filter((sp) => sp?.part?.description)
    : [];

  const repairedKey = String(repair.repairedStatus || '').trim();
  const repairedMeta = REPAIRED_STATUS_LABELS[repairedKey];

  const paymentAmount = Number(repair.paymentAmount || 0);
  const showPrice = paymentAmount > 0.009;
  const paymentDisplay = resolveRepairPaymentDisplayState(repair);
  const isPaymentCompleted = paymentDisplay.isCompleted;
  const paymentDue = repair.paymentDue === true || (showPrice && !isPaymentCompleted);
  const paidByCustomer = Boolean(repair.paymentPaidByCustomer);
  const statusDates = useMemo(() => buildRepairStatusDates(repair), [repair]);
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);

  const handlePublicPay = async () => {
    if (!publicTrackMode || !paymentDue || paying) return;
    setPayError(null);
    setPaying(true);
    try {
      const { redirectUrl } = await paymentService.createVerifoneCheckoutFromPublicTrack(repair);
      if (!redirectUrl) throw new Error('לא התקבל קישור תשלום');
      window.location.assign(redirectUrl);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'שגיאה בפתיחת דף התשלום';
      setPayError(message);
      setPaying(false);
    }
  };

  return (
    <Stack spacing={2} sx={{ width: '100%', direction: 'rtl' }}>
      {/* כותרת + סטטוס */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          background: (t) =>
            t.palette.mode === 'dark'
              ? `linear-gradient(135deg, ${alpha(t.palette.primary.main, 0.16)} 0%, ${t.palette.background.paper} 72%)`
              : `linear-gradient(135deg, ${alpha(t.palette.primary.main, 0.08)} 0%, #ffffff 55%)`,
        }}
      >
        <Box sx={{ px: { xs: 1.75, sm: 2.5 }, pt: 2, pb: 1.5 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            spacing={1.5}
            sx={{ mb: 1.5 }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'baseline',
                gap: 2.5,
                direction: 'rtl',
                flexWrap: 'wrap',
              }}
            >
              <Typography
                variant="overline"
                sx={{
                  color: 'text.secondary',
                  letterSpacing: 1.2,
                  lineHeight: 1.2,
                  flexShrink: 0,
                  pb: 0.25,
                }}
              >
                מספר קריאה
              </Typography>
              {copyableCallNumber ? (
                <CopyableText
                  text={copyableCallNumber}
                  tooltipCopy="לחץ להעתקת מספר קריאה"
                  tooltipCopied="מספר הקריאה הועתק!"
                >
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      direction: 'ltr',
                    }}
                  >
                    <Typography
                      component="span"
                      variant="h5"
                      sx={{
                        fontWeight: 800,
                        color: 'primary.main',
                        direction: 'ltr',
                        unicodeBidi: 'embed',
                        letterSpacing: '0.04em',
                        lineHeight: 1.2,
                      }}
                    >
                      {displayCallNumber}
                    </Typography>
                    <ContentCopyIcon
                      sx={{
                        fontSize: 18,
                        color: 'primary.main',
                        opacity: 0.55,
                        flexShrink: 0,
                      }}
                    />
                  </Box>
                </CopyableText>
              ) : (
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 800,
                    color: 'primary.main',
                    direction: 'ltr',
                    unicodeBidi: 'embed',
                    letterSpacing: '0.04em',
                    lineHeight: 1.2,
                  }}
                >
                  {displayCallNumber}
                </Typography>
              )}
            </Box>
            <Chip
              label={statusLabel}
              sx={{
                bgcolor: statusColors.bg,
                color: statusColors.text,
                fontWeight: 700,
                fontSize: '0.875rem',
                height: 34,
                px: 0.5,
                ...(statusColors.border ? { border: statusColors.border } : {}),
              }}
            />
          </Stack>
          <StatusStepper
            status={repair.status}
            onChange={() => {}}
            disabled
            compact
            customerFacing
            statusDates={statusDates}
          />
          {returnedToCustomerAt && (
            <Typography
              variant="body2"
              sx={{
                mt: 1.25,
                pt: 1.25,
                borderTop: '1px solid',
                borderColor: 'divider',
                color: 'text.secondary',
                fontSize: '0.8125rem',
              }}
            >
              חזר ללקוח / לחנות:{' '}
              <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                {returnedToCustomerAt}
              </Box>
            </Typography>
          )}
        </Box>
      </Paper>

      {/* לקוח + מכשיר */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 2,
        }}
      >
        <SectionCard
          icon={<PersonOutlineIcon fontSize="small" />}
          title="פרטי לקוח"
          headerExtra={
            onOpenRepairHistory ? (
              <Button
                size="small"
                variant="outlined"
                color="primary"
                onClick={onOpenRepairHistory}
                startIcon={<HistoryIcon sx={{ fontSize: 18 }} />}
                sx={{
                  height: 30,
                  minHeight: 30,
                  fontWeight: 700,
                  px: 1.5,
                  whiteSpace: 'nowrap',
                  borderRadius: 1.5,
                  flexDirection: 'row-reverse',
                  gap: 0.5,
                  bgcolor: (t) => alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.14 : 0.08),
                  borderColor: 'primary.light',
                  color: 'primary.main',
                  boxShadow: (t) => `0 1px 3px ${alpha(t.palette.primary.main, 0.18)}`,
                  '&:hover': {
                    bgcolor: (t) => alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.22 : 0.14),
                    borderColor: 'primary.main',
                  },
                }}
              >
                היסטוריית תיקונים
              </Button>
            ) : null
          }
        >
          <InfoRow label="שם">
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {customerName}
            </Typography>
          </InfoRow>
          {String(customer?.company || repair.customerCompany || '').trim() ? (
            <InfoRow label="חברה">
              <Typography variant="body2">{customer?.company || repair.customerCompany}</Typography>
            </InfoRow>
          ) : null}
          <InfoRow label="טלפון">
            {phone ? (
              /[•*]/.test(phone) || !/\d{7,}/.test(phone.replace(/\D/g, '')) ? (
                <Typography variant="body2" sx={{ direction: 'ltr', unicodeBidi: 'embed' }}>
                  {phone}
                </Typography>
              ) : (
                <PhoneLinkChip phone={phone} variant="customer" size="small" />
              )
            ) : (
              <Typography variant="body2">—</Typography>
            )}
          </InfoRow>
          {String(customer?.city || '').trim() ? (
            <InfoRow label="עיר">
              <Typography variant="body2">{customer?.city}</Typography>
            </InfoRow>
          ) : null}
          <InfoRow label="חנות" hideDivider>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {repair.storeName || '—'}
            </Typography>
          </InfoRow>
        </SectionCard>

        <SectionCard icon={<DevicesOtherOutlinedIcon fontSize="small" />} title="פרטי המכשיר">
          <InfoRow label="דגם">
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {deviceLabel}
            </Typography>
          </InfoRow>
          <InfoRow label="מס' סידורי">
            <Typography variant="body2" sx={{ direction: 'ltr', unicodeBidi: 'embed', display: 'inline-block' }}>
              {repair.serialNumber || '—'}
            </Typography>
          </InfoRow>
          <InfoRow label="תאריך קליטה">
            <Typography variant="body2">
              {repair.receivedDate ? formatDateTime(repair.receivedDate) : '—'}
            </Typography>
          </InfoRow>
          {accessories.length > 0 ? (
            <InfoRow label="ציוד נלווה" hideDivider>
              <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.5}>
                {accessories.map((item) => (
                  <Chip key={item} label={item} size="small" variant="outlined" />
                ))}
              </Stack>
            </InfoRow>
          ) : (
            <InfoRow label="ציוד נלווה" hideDivider>
              <Typography variant="body2" color="text.secondary">
                —
              </Typography>
            </InfoRow>
          )}
        </SectionCard>
      </Box>

      {/* תקלה */}
      {showIssueSection && (
        <SectionCard icon={<ReportProblemOutlinedIcon fontSize="small" />} title="פירוט התקלה">
          {issueTypes.length > 0 && (
            <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.75} sx={{ py: 1 }}>
              {issueTypes.map((type) => (
                <Chip key={type} label={type} size="small" color="warning" variant="outlined" />
              ))}
            </Stack>
          )}
          {showIssueText && (
            <>
              {issueTypes.length > 0 && <Divider sx={{ mb: 1 }} />}
              <Typography
                variant="body2"
                sx={{
                  py: 1,
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.65,
                  color: 'text.primary',
                }}
              >
                {issueText}
              </Typography>
            </>
          )}
          {!publicTrackMode && issueWrittenAt && (
            <Box sx={{ pb: 0.75 }}>
              <DateCaption>נכתב: {issueWrittenAt}</DateCaption>
            </Box>
          )}
        </SectionCard>
      )}

      {/* סיכום הטיפול */}
      {(repairedMeta || treatmentSummary || spareParts.length > 0) && (
        <SectionCard
          icon={<BuildOutlinedIcon fontSize="small" />}
          title="סיכום הטיפול"
          headerExtra={
            repairedMeta ? (
              <Chip
                label={repairedMeta.label}
                color={repairedMeta.color}
                size="small"
                sx={{ fontWeight: 700, height: 26 }}
              />
            ) : undefined
          }
        >
          {treatmentSummary && (
            <Box sx={{ py: 0.75 }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.65 }}>
                {treatmentSummary}
              </Typography>
              {treatmentSummaryAt && (
                <DateCaption>נכתב: {treatmentSummaryAt}</DateCaption>
              )}
            </Box>
          )}
          {spareParts.length > 0 && (
            <InfoRow label="חלקים" hideDivider>
              <Stack spacing={0.75}>
                {spareParts.map(({ part, quantity }) => (
                  <Box
                    key={part.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      py: 0.5,
                      px: 1,
                      borderRadius: 1.5,
                      bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'),
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {part.description}
                    </Typography>
                    <Chip label={`×${quantity}`} size="small" variant="outlined" sx={{ minWidth: 44 }} />
                  </Box>
                ))}
              </Stack>
            </InfoRow>
          )}
        </SectionCard>
      )}

      {/* מחיר / תשלום */}
      {showPrice && (
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 2.5,
            overflow: 'hidden',
            borderColor: (t) =>
              isPaymentCompleted
                ? t.palette.mode === 'dark'
                  ? 'rgba(76,175,80,0.45)'
                  : 'rgba(46,125,50,0.35)'
                : t.palette.mode === 'dark'
                  ? 'rgba(255,208,0,0.35)'
                  : 'rgba(230,187,0,0.45)',
            bgcolor: (t) =>
              isPaymentCompleted
                ? t.palette.mode === 'dark'
                  ? 'rgba(46,125,50,0.14)'
                  : 'rgba(46,125,50,0.07)'
                : t.palette.mode === 'dark'
                  ? 'rgba(255,208,0,0.08)'
                  : 'rgba(230,187,0,0.08)',
          }}
        >
          <Box sx={{ px: 2, py: 1.75 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between"
              spacing={1.5}
            >
              <Stack direction="row" alignItems="flex-start" spacing={1.25} sx={{ minWidth: 0 }}>
                <PaymentsOutlinedIcon
                  sx={{
                    mt: 0.25,
                    color: isPaymentCompleted ? 'success.main' : 'primary.main',
                    fontSize: 26,
                  }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.3 }}>
                    {isPaymentCompleted ? 'תשלום' : 'מחיר לגבייה'}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.75, flexWrap: 'wrap' }}>
                    {isPaymentCompleted ? (
                      <Chip
                        icon={<CheckCircleOutlineIcon sx={{ fontSize: '18px !important' }} />}
                        label={paidByCustomer ? 'שולם על ידי הלקוח' : 'נגבה'}
                        size="small"
                        color="success"
                        sx={{ fontWeight: 700 }}
                      />
                    ) : (
                      <Chip
                        label="טרם נגבה"
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{ fontWeight: 700 }}
                      />
                    )}
                    {isPaymentCompleted && (repair.paymentCompletedAt || repair.paymentDate) && (
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(repair.paymentCompletedAt || repair.paymentDate)}
                      </Typography>
                    )}
                  </Stack>
                </Box>
              </Stack>

              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  color: isPaymentCompleted ? 'success.dark' : 'text.primary',
                  direction: 'ltr',
                  unicodeBidi: 'embed',
                  alignSelf: { xs: 'flex-start', sm: 'center' },
                  lineHeight: 1.2,
                }}
              >
                ₪{paymentAmount.toFixed(2)}
              </Typography>
            </Stack>

            {publicTrackMode && paymentDue && (
              <Box sx={{ mt: 1.75 }}>
                {payError && (
                  <Alert severity="error" sx={{ mb: 1.25, textAlign: 'right' }}>
                    {payError}
                  </Alert>
                )}
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={paying || !repair.id}
                  onClick={() => void handlePublicPay()}
                  startIcon={
                    paying ? <CircularProgress size={18} color="inherit" /> : <CreditCardOutlinedIcon />
                  }
                  sx={{
                    fontWeight: 800,
                    py: 1.15,
                    borderRadius: 2,
                    fontSize: '1rem',
                    gap: 1.25,
                    '& .MuiButton-startIcon': {
                      margin: 0,
                      marginInlineEnd: '10px',
                    },
                  }}
                >
                  {paying ? 'פותח דף תשלום...' : 'לתשלום מאובטח'}
                </Button>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 1, textAlign: 'center' }}
                >
                  מעבר לדף סליקה מאובטח
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      )}

      {repair.storeConfirmed && repair.storeConfirmedAt ? (
        <Typography variant="caption" color="success.main" sx={{ textAlign: 'center', display: 'block' }}>
          ✓ אושרה קבלה בחנות — {formatDateTime(repair.storeConfirmedAt)}
        </Typography>
      ) : null}
    </Stack>
  );
};

const ExternalRepairView: React.FC<ExternalRepairViewProps> = (props) => {
  const { mode } = useThemeMode();
  if (props.publicTrackMode) {
    return (
      <ThemeProvider theme={mode === 'dark' ? publicTrackThemeDark : publicTrackThemeLight}>
        <ExternalRepairViewInner {...props} />
      </ThemeProvider>
    );
  }
  return <ExternalRepairViewInner {...props} />;
};

export default ExternalRepairView;
